import { useEffect, useRef, useState } from 'react';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Check, RotateCw, X } from 'lucide-react';
import { girarImagen, procesarPagina, type Recorte } from '../../utils/comprobanteImagen';
import { ERROR_TEXT } from '../ui/tokens';

const RECORTE_COMPLETO: Crop = { unit: '%', x: 0, y: 0, width: 100, height: 100 };
const BOTON = 'inline-flex items-center gap-1.5 min-h-11 px-4 rounded-xl text-sm font-medium disabled:opacity-50';

interface AjusteFotoComprobanteProps {
  readonly foto: Blob;
  readonly onListo: (pagina: Blob) => void;
  readonly onCancelar: () => void;
}

export const AjusteFotoComprobante: React.FC<AjusteFotoComprobanteProps> = ({ foto, onListo, onCancelar }) => {
  // Solo hay foto girada después de girar; mientras tanto se trabaja con la original.
  const [girada, setGirada] = useState<Blob | null>(null);
  const actual = girada ?? foto;
  const [crop, setCrop] = useState<Crop>(RECORTE_COMPLETO);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState('');
  const imgRef = useRef<HTMLImageElement>(null);
  // El recuadro en puntos de pantalla solo se lee al terminar o girar, no pinta nada: va en una referencia.
  const recortePantallaRef = useRef<PixelCrop | null>(null);

  // La URL se crea y se revoca en el mismo efecto, así nunca queda una sin liberar.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return undefined;
    const url = URL.createObjectURL(actual);
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [actual]);

  /** El recuadro está en puntos de pantalla; el recorte se hace sobre la imagen real. */
  const recorteReal = (img: HTMLImageElement): Recorte | null => {
    const recorte = recortePantallaRef.current;
    if (!recorte || recorte.width === 0 || recorte.height === 0) return null;
    const escalaX = img.naturalWidth / img.width;
    const escalaY = img.naturalHeight / img.height;
    return {
      x: recorte.x * escalaX,
      y: recorte.y * escalaY,
      ancho: recorte.width * escalaX,
      alto: recorte.height * escalaY,
    };
  };

  /** Devuelve lo que produce la tarea, o null si no había imagen o falló (el error ya quedó en pantalla). */
  const ejecutar = async <T,>(tarea: (img: HTMLImageElement) => Promise<T>): Promise<T | null> => {
    const img = imgRef.current;
    if (!img) return null;
    setTrabajando(true);
    setError('');
    try {
      return await tarea(img);
    } catch {
      setError('No pudimos procesar la foto. Intenta de nuevo.');
      return null;
    } finally {
      setTrabajando(false);
    }
  };

  const girar = async () => {
    const fotoGirada = await ejecutar((img) => girarImagen(img, 90));
    if (!fotoGirada) return;
    setGirada(fotoGirada);
    setCrop(RECORTE_COMPLETO);
    recortePantallaRef.current = null;
  };

  const terminar = (recortar: boolean) => ejecutar(async (img) => {
    onListo(await procesarPagina(img, recortar ? recorteReal(img) : null));
  });

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 p-3">
      <p className="text-xs text-slate-500">Arrastra las esquinas del recuadro para dejar solo el documento. Cuando esté listo, toca «Listo».</p>
      <ReactCrop crop={crop} onChange={(_, porcentaje) => { setCrop(porcentaje); }} onComplete={(recorte) => { recortePantallaRef.current = recorte; }} keepSelection
        // 22rem reserva el alto del modal, la etiqueta, la ayuda y la barra de botones, para que la foto entera quepa en pantalla.
        // El tope va en el contenedor: la hoja de react-image-crop hace que la imagen herede su max-height.
        className="max-h-[calc(100vh-22rem)] supports-[height:100dvh]:max-h-[calc(100dvh-22rem)]">
        <img ref={imgRef} alt="Foto del comprobante para recortar" className="w-auto" />
      </ReactCrop>
      {error && <p className={ERROR_TEXT} role="alert">{error}</p>}
      {/* Fija al borde inferior del modal: se ve siempre, sin desplazar la página. */}
      <div className="sticky bottom-0 z-10 -mx-3 -mb-3 flex flex-wrap gap-2 rounded-b-xl border-t border-slate-200 bg-white px-3 py-2">
        <button type="button" onClick={() => { void girar(); }} disabled={trabajando}
          className={`${BOTON} border border-slate-200 text-slate-700`}>
          <RotateCw className="w-4 h-4" aria-hidden="true" /> Girar
        </button>
        <button type="button" onClick={() => { void terminar(false); }} disabled={trabajando}
          className={`${BOTON} border border-slate-200 text-slate-700`}>
          Usar sin recortar
        </button>
        <button type="button" onClick={() => { void terminar(true); }} disabled={trabajando}
          className={`${BOTON} bg-blue-600 text-white`}>
          <Check className="w-4 h-4" aria-hidden="true" /> Listo
        </button>
        <button type="button" onClick={onCancelar} disabled={trabajando}
          className={`${BOTON} text-slate-600`}>
          <X className="w-4 h-4" aria-hidden="true" /> Cancelar
        </button>
      </div>
    </div>
  );
};
